import { Trans } from '@lingui/react';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import styles from '../../cards/inlineProduct/InlineProductCard.module.scss';
import { ProductCardBadge } from '../../cards/product/ProductCardBadge';
import {
  AddToCart,
  Button,
  QuickAddToCartStates,
  ProductPrice,
} from '../../ui';
import { LoadingText } from '../../ui/loading/LoadingText';
import productPriceStyles from '../../ui/productPrice/ProductPrice.module.scss';
import type { InlineProductCardProduct } from '../inlineProduct/InlineProductCard';
import recommendedEssentialCardStyles from './RecommendedEssentialCard.module.scss';

export type RecommendedEssentialCardProps = {
  product?: InlineProductCardProduct;
  isLoading?: boolean;
  className?: string;
  handleAdd?: (payload: { id: string; quantity: number }) => void;
  proCustomer?: boolean;
  amazonCart?: boolean;
  quickAddToCartState?: QuickAddToCartStates;
  variantAlreadyInCart?: boolean;
  /**
   * Is it the Product that has Recommended essentials attached to.
   */
  isMainProduct?: boolean;
  cartEnabled?: boolean;
  showPrice?: boolean;
  mikmakEnabled?: boolean;
};

export const RecommendedEssentialCardMobile = (
  props: RecommendedEssentialCardProps
) => {
  const { product, isLoading } = props;

  if (!product || isLoading) {
    return <RecommendedEssentialCardMobileLoading {...props} />;
  }

  return (
    <RecommendedEssentialCardMobileActive
      {...props}
      product={product}
      isLoading={false}
    />
  );
};

export const RecommendedEssentialCardMobileActive = (
  props: RecommendedEssentialCardProps & {
    product: InlineProductCardProduct;
    isLoading: false;
  }
) => {
  const {
    product,
    isLoading,
    className,
    handleAdd,
    proCustomer = false,
    amazonCart = false,
    quickAddToCartState,
    variantAlreadyInCart,
    isMainProduct,
    cartEnabled,
    showPrice,
    mikmakEnabled,
  } = props;

  const proTest = !!product?.proOnly && !proCustomer;

  const handleAddClick = () => {
    const id = product.firstAvailableVariantId;
    if (!id) {
      return;
    }

    if (handleAdd) {
      handleAdd({
        id,
        quantity: 1,
      });
    }
  };

  return (
    <div
      className={clsx(
        recommendedEssentialCardStyles.recommendedEssentialCard,
        className,
        mikmakEnabled && 'product-quickview'
      )}
    >
      <div className={styles.inlineProductCard__imageWrapper}>
        {product?.image && (
          <Image
            className={styles.inlineProductCard__image}
            src={product.image}
            width={57}
            height={74}
            alt=""
          />
        )}
      </div>
      <div className={styles.inlineProductCard__content}>
        <ProductCardBadge
          product={product}
          className={styles.inlineProductCard__badge}
        />

        {isMainProduct && product?.systemName && (
          <p
            className={clsx(
              styles.inlineProductCard__systemName,
              'text-utility-utility-small-mobile',
              'text-utility-utility-desktop'
            )}
          >
            {product.systemName}
          </p>
        )}

        {product?.title && (
          <p
            className={clsx(
              styles.inlineProductCard__title,
              'text-body-2-bold-mobile',
              'text-body-2-bold-desktop'
            )}
          >
            {product.title}
          </p>
        )}

        {!proTest && showPrice && (
          <ProductPrice
            price={product.price}
            type="unit"
            isLoading={isLoading}
            className="text-body-2-mobile"
          />
        )}

        {!isMainProduct && !variantAlreadyInCart && (
          <div className={styles.inlineProductCard__ctaWrapper}>
            <AddToCart
              product={{
                ...product,
                barcode: product?.barcode,
              }}
              proCustomer={proCustomer}
              amazonCart={amazonCart}
              placement="recommended_essentials_card"
              addToCart={handleAddClick}
              state={quickAddToCartState}
              cartEnabled={cartEnabled}
              mikmakEnabled={mikmakEnabled}
            />
          </div>
        )}

        {!isMainProduct && variantAlreadyInCart && (
          <div className={styles.inlineProductCard__ctaWrapper}>
            <p className="text-utility-utility-mobile">
              <Trans id="recommendedEssentials.added">Added!</Trans>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export const RecommendedEssentialCardMobileLoading = (
  props: RecommendedEssentialCardProps
) => {
  const { product, className } = props;

  return (
    <div className={clsx(styles.inlineProductCard, className, 'is-loading')}>
      <div className={styles.inlineProductCard__imageWrapper}>
        <div className={styles.inlineProductCard__image}></div>
      </div>
      <div className={styles.inlineProductCard__content}>
        <p
          className={clsx(
            styles.inlineProductCard__systemName,
            'text-utility-utility-small-mobile',
            'text-utility-utility-desktop'
          )}
          aria-hidden
        >
          {product?.systemName ?? <LoadingText />}
        </p>

        <p
          className={clsx(
            styles.inlineProductCard__title,
            'text-body-2-bold-mobile',
            'text-body-1-bold-desktop'
          )}
          aria-hidden
        >
          {product?.title ?? <LoadingText />}
        </p>

        <p className={productPriceStyles['productPrice--unit']} aria-hidden>
          <span className={styles.inlineProductCard__price}>
            <LoadingText />
          </span>
        </p>
      </div>
    </div>
  );
};

export const RecommendedEssentialCardDesktop = (
  props: RecommendedEssentialCardProps
) => {
  const {
    product,
    proCustomer,
    amazonCart,
    isLoading,
    quickAddToCartState = QuickAddToCartStates.Active,
    variantAlreadyInCart = false,
  } = props;

  if (!product || isLoading) {
    return <RecommendedEssentialCardDesktopLoading {...props} />;
  }

  return (
    <RecommendedEssentialCardDesktopActive
      {...props}
      product={product}
      proCustomer={proCustomer}
      amazonCart={amazonCart}
      isLoading={false}
      quickAddToCartState={quickAddToCartState}
      variantAlreadyInCart={variantAlreadyInCart}
    />
  );
};

export const RecommendedEssentialCardDesktopActive = (
  props: RecommendedEssentialCardProps
) => {
  const {
    product,
    isLoading = false,
    className,
    handleAdd,
    proCustomer = false,
    amazonCart = false,
    quickAddToCartState,
    variantAlreadyInCart,
    showPrice,
    cartEnabled,
    mikmakEnabled,
  } = props;

  const proTest = !!product?.proOnly && !proCustomer;

  const handleAddClick = () => {
    const id = product?.firstAvailableVariantId;
    if (!id) {
      return;
    }

    if (handleAdd) {
      handleAdd({
        id,
        quantity: 1,
      });
    }
  };

  if (!product) {
    return null;
  }

  return (
    <div
      className={clsx(
        styles.inlineProductCard,
        recommendedEssentialCardStyles.recommendedEssentialCardDesktop,
        className,
        mikmakEnabled && 'product-quickview'
      )}
    >
      <div className={styles.inlineProductCard__imageWrapper}>
        {product?.image && (
          <Image
            className={clsx(
              styles.inlineProductCard__image,
              recommendedEssentialCardStyles.recommendedEssentialCardDesktop__image
            )}
            src={product.image}
            width={57}
            height={73}
            alt=""
          />
        )}
      </div>
      <div className={styles.inlineProductCard__content}>
        {product?.systemName && (
          <p
            className={clsx(
              styles.inlineProductCard__systemName,
              'text-utility-utility-small-mobile',
              'text-utility-utility-desktop'
            )}
          >
            {product.systemName}
          </p>
        )}

        {product?.title && (
          <Link
            className={clsx(
              styles.inlineProductCard__title,
              'text-body-2-bold-mobile',
              'text-body-2-bold-desktop'
            )}
            href={product.url}
          >
            {product.title}
          </Link>
        )}

        {product?.slogan && (
          <p
            className={clsx(
              styles.inlineProductCard__slogan,
              recommendedEssentialCardStyles.recommendedEssentialCardDesktop__slogan,
              'text-body-small-desktop text-body-small-mobile'
            )}
          >
            {product.slogan}
          </p>
        )}

        {!proTest && showPrice && (
          <ProductPrice
            price={product.price}
            type="unit"
            isLoading={isLoading}
            className="text-body-2"
          />
        )}
      </div>

      <div className={styles.inlineProductCard__ctaWrapper}>
        <AddToCart
          product={{
            ...product,
            barcode: product?.barcode,
          }}
          proCustomer={proCustomer}
          amazonCart={amazonCart}
          placement="inline_card"
          addToCart={handleAddClick}
          state={quickAddToCartState}
          variantAlreadyInCart={variantAlreadyInCart}
          cartEnabled={cartEnabled}
          mikmakEnabled={mikmakEnabled}
        />
      </div>
    </div>
  );
};

export const RecommendedEssentialCardDesktopLoading = (
  props: RecommendedEssentialCardProps
) => {
  const { product, className } = props;

  return (
    <div className={clsx(styles.inlineProductCard, className, 'is-loading')}>
      <div className={styles.inlineProductCard__imageWrapper}>
        <div className={styles.inlineProductCard__image}></div>
      </div>
      <div className={styles.inlineProductCard__content}>
        <p
          className={clsx(
            styles.inlineProductCard__systemName,
            'text-utility-utility-small-mobile',
            'text-utility-utility-desktop'
          )}
          aria-hidden
        >
          {product?.systemName ?? <LoadingText />}
        </p>

        <p
          className={clsx(
            styles.inlineProductCard__title,
            'text-body-2-bold-mobile',
            'text-body-1-bold-desktop'
          )}
          aria-hidden
        >
          {product?.title ?? <LoadingText />}
        </p>

        <p className={productPriceStyles['productPrice--unit']} aria-hidden>
          <span className={styles.inlineProductCard__price}>
            <LoadingText />
          </span>
        </p>
      </div>

      <div className={styles.inlineProductCard__ctaWrapper}>
        <Button type={'button'} modifiers={['mini']} disabled>
          <Trans id="product.addToCart">ADD TO BAG</Trans>
        </Button>
      </div>
    </div>
  );
};
